/**
 * Feed→screen latency probe — the F3 instrument (sibling of
 * `orderLatencyProbe`, same posture: ring of samples, `performance.now()`
 * marks, `console.table` dump, nothing spent while nobody reads it).
 *
 * The single-game live view is latency-first by construction, but until now
 * "book tick → pixels" was an estimate (≈25–66 ms, all client-side: the
 * 33 ms flush throttle + rAF + paint). This probe stamps ONE frame's walk
 * through the pipeline so the estimate becomes a measurement:
 *
 *   wire     hydra `serverTs` (fallback: `book.timestamp`) → ingest.
 *            Wall-clock, so it carries the box↔browser clock skew — read it
 *            as a trend, not an absolute. Null when the frame had no stamp
 *            (a client that predates the widened onOrderbook callback).
 *   ingest   `marketStore.ingestOrderbook/ingestMarket` → the throttled
 *            flush (`notifyGameTicks`). This is the MIN_FLUSH_INTERVAL_MS
 *            coalesce window showing up as latency.
 *   flush    flush → end of the `useLiveGameBoard` derive (the memo). The
 *            store's derive cost — getGames/getGameEvents/getPeriodMoneyline
 *            — sits in here.
 *   derive   derive → the next animation frame after the LiveGame commit.
 *            An rAF chained off the commit fires at the NEXT frame boundary,
 *            so this is an upper bound on when the pixels landed (≤ 1 frame
 *            over).
 *   total    ingest → paint.
 *
 * GATE: only tickers a live view has REGISTERED (`watchFeedTickers`) are
 * stamped. The fleet delivers thousands of book frames a second for games
 * nobody is on; for those the ingest hook is a single `Map.size` read.
 *
 * Coalescing: several frames for one ticker can land inside one flush
 * window. The FIRST one is the sample (it waited longest — that is the
 * latency the trader sees); the rest bump `coalesced` on it.
 *
 * A flushed batch that never reaches a paint mark (view unmounted mid-tick,
 * or a flush with no live listener) is finalized as-is on the next flush
 * with null marks for the stages it never reached — the nulls are the
 * evidence, not an error.
 *
 * `window.__pushLatency()` is the relay-side twin: the SSE envelope's `ts`
 * (relay emit) → `relayPush.dispatch` (apply), per topic.
 */

import { gatewayHealth } from '../feed/gatewayHealthMeter';

export type FeedFrameKind = 'orderbook' | 'market' | 'opOdds';

/** Probe key for a game's OddsPapi frames. The watched set is keyed by ticker;
 *  odds are keyed by game, so they ride a prefixed key that cannot collide with
 *  a venue ticker. `wire` for this kind is core's own apply stamp
 *  (`OpGameOdds.lastUpdateMs`) → browser ingest: the whole core emit path, the
 *  consumer queue, the network, the worker and the per-tab clone. Read it as a
 *  trend (it carries box↔browser clock skew), never as a displayed value. */
export function opProbeKey(gameKey: string): string {
  return `op:${gameKey}`;
}

export interface FeedLatencySample {
  ticker: string;
  kind: FeedFrameKind;
  /** Frames for this ticker that landed in the same flush window (1 = none). */
  coalesced: number;
  /** serverTs (or book.timestamp) → ingest, wall-clock ms; null without a stamp. */
  wire: number | null;
  /** hydra's own `staleness.ageMs` when the frame carried it. */
  serverAge: number | null;
  /** ingest → flush (notifyGameTicks). */
  ingest: number | null;
  /** flush → end of the useLiveGameBoard derive. */
  flush: number | null;
  /** derive → next animation frame after the LiveGame commit. */
  derive: number | null;
  /** ingest → paint. */
  total: number | null;
  /** performance.now() at ingest (monotonic — compare marks, not this). */
  ingestedAt: number;
}

export interface FeedStageStats {
  stage: 'wire' | 'ingest' | 'flush' | 'derive' | 'total';
  n: number;
  p50: number | null;
  p95: number | null;
  max: number | null;
}

interface Pending {
  ticker: string;
  kind: FeedFrameKind;
  coalesced: number;
  wire: number | null;
  serverAge: number | null;
  ingestAt: number;
}

interface Batch {
  frames: Pending[];
  flushAt: number;
  deriveAt: number | null;
  paintAt: number | null;
}

export const FEED_LATENCY_RING_MAX = 200;
const PUSH_RING_MAX = 500;

const ring: FeedLatencySample[] = [];
/** ticker → refcount of live views watching it. */
const watched = new Map<string, number>();
let pending = new Map<string, Pending>();
let inFlight: Batch | null = null;

// ── Watched-ticker gate ─────────────────────────────────────────────

/** Register the tickers a live view renders. Refcounted; returns the release.
 *  Empty input is a no-op release. */
export function watchFeedTickers(tickers: readonly string[]): () => void {
  const mine = [...new Set(tickers)];
  for (const t of mine) watched.set(t, (watched.get(t) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const t of mine) {
      const n = (watched.get(t) ?? 1) - 1;
      if (n <= 0) {
        watched.delete(t);
        pending.delete(t);
      } else {
        watched.set(t, n);
      }
    }
  };
}

export function isFeedTickerWatched(ticker: string): boolean {
  return watched.has(ticker);
}

// ── Stage marks ─────────────────────────────────────────────────────

/** Ingest mark. `wireRef` is the producer's stamp — hydra `serverTs` when the
 *  client forwards it, else the book's own `timestamp`; both epoch ms. */
export function probeFeedIngest(
  ticker: string,
  kind: FeedFrameKind,
  wireRef?: number | null,
  staleness?: { ageMs?: number } | null,
): void {
  if (watched.size === 0 || !watched.has(ticker)) return;
  const prev = pending.get(ticker);
  if (prev) {
    prev.coalesced += 1;
    return;
  }
  const wire =
    typeof wireRef === 'number' && Number.isFinite(wireRef) && wireRef > 0
      ? Date.now() - wireRef
      : null;
  const serverAge =
    staleness && typeof staleness.ageMs === 'number' && Number.isFinite(staleness.ageMs)
      ? staleness.ageMs
      : null;
  pending.set(ticker, { ticker, kind, coalesced: 1, wire, serverAge, ingestAt: performance.now() });
}

/** Flush mark — `notifyGameTicks`. Moves every pending frame into the
 *  in-flight batch; a still-open previous batch is finalized as-is. */
export function probeFeedFlush(): void {
  if (pending.size === 0) return;
  if (inFlight) finalize(inFlight);
  inFlight = { frames: [...pending.values()], flushAt: performance.now(), deriveAt: null, paintAt: null };
  pending = new Map();
}

/** Derive mark — end of the useLiveGameBoard memo. First derive per batch wins. */
export function probeFeedDerive(): void {
  if (!inFlight || inFlight.deriveAt != null) return;
  inFlight.deriveAt = performance.now();
}

/** Paint mark — the rAF chained off the LiveGame commit. Closes the batch. */
export function probeFeedPaint(): void {
  if (!inFlight) return;
  inFlight.paintAt = performance.now();
  finalize(inFlight);
  inFlight = null;
}

function finalize(batch: Batch): void {
  const { flushAt, deriveAt, paintAt } = batch;
  for (const f of batch.frames) {
    ring.push({
      ticker: f.ticker,
      kind: f.kind,
      coalesced: f.coalesced,
      wire: f.wire,
      serverAge: f.serverAge,
      ingest: flushAt - f.ingestAt,
      flush: deriveAt != null ? deriveAt - flushAt : null,
      derive: deriveAt != null && paintAt != null ? paintAt - deriveAt : null,
      total: paintAt != null ? paintAt - f.ingestAt : null,
      ingestedAt: f.ingestAt,
    });
  }
  while (ring.length > FEED_LATENCY_RING_MAX) ring.shift();
}

// ── Readers ─────────────────────────────────────────────────────────

export function feedLatencySamples(): FeedLatencySample[] {
  return [...ring];
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[idx];
}

function round(v: number | null): number | null {
  return v == null ? null : Math.round(v * 10) / 10;
}

export function feedLatencyStats(kind?: FeedFrameKind): FeedStageStats[] {
  const stages: FeedStageStats['stage'][] = ['wire', 'ingest', 'flush', 'derive', 'total'];
  const rows = kind == null ? ring : ring.filter((s) => s.kind === kind);
  return stages.map((stage) => {
    const vals = rows
      .map((s) => s[stage])
      .filter((v): v is number => v != null)
      .sort((a, b) => a - b);
    return {
      stage,
      n: vals.length,
      p50: round(percentile(vals, 0.5)),
      p95: round(percentile(vals, 0.95)),
      max: round(vals.length ? vals[vals.length - 1] : null),
    };
  });
}

/** Tests only — drop every sample, watch and in-flight mark. */
export function resetFeedLatencyProbe(): void {
  ring.length = 0;
  watched.clear();
  pending = new Map();
  inFlight = null;
  pushRing.length = 0;
}

// ── Relay push twin ─────────────────────────────────────────────────

export interface PushLatencySample {
  topic: string;
  /** relay `ts` (envelope) → dispatch, wall-clock ms. */
  latency: number;
  at: number;
}

export interface PushTopicStats {
  topic: string;
  n: number;
  p50: number | null;
  p95: number | null;
  max: number | null;
  last: number | null;
}

const pushRing: PushLatencySample[] = [];

/** Apply mark for one SSE frame; `ts` is the relay's emit stamp (epoch ms). */
export function probePushApply(topic: string, ts: number): void {
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return;
  const now = Date.now();
  pushRing.push({ topic, latency: now - ts, at: now });
  while (pushRing.length > PUSH_RING_MAX) pushRing.shift();
}

export function pushLatencySamples(): PushLatencySample[] {
  return [...pushRing];
}

export function pushLatencyStats(): PushTopicStats[] {
  const byTopic = new Map<string, number[]>();
  const last = new Map<string, number>();
  for (const s of pushRing) {
    let arr = byTopic.get(s.topic);
    if (!arr) byTopic.set(s.topic, (arr = []));
    arr.push(s.latency);
    last.set(s.topic, s.latency);
  }
  return [...byTopic.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([topic, vals]) => {
      const sorted = [...vals].sort((a, b) => a - b);
      return {
        topic,
        n: sorted.length,
        p50: round(percentile(sorted, 0.5)),
        p95: round(percentile(sorted, 0.95)),
        max: round(sorted[sorted.length - 1] ?? null),
        last: round(last.get(topic) ?? null),
      };
    });
}

// ── Console hooks ───────────────────────────────────────────────────

declare global {
  interface Window {
    /** Dump the feed→screen stage table; returns the raw samples. */
    __feedLatency?: () => FeedLatencySample[];
    /** Dump relay-push apply latency per topic; returns the raw samples. */
    __pushLatency?: () => PushLatencySample[];
  }
}

if (typeof window !== 'undefined') {
  window.__feedLatency = () => {
    /* eslint-disable no-console -- the probe's whole job */
    console.log(
      `[feedLatency] ${ring.length} samples (ring ${FEED_LATENCY_RING_MAX}) · watching ${watched.size} ticker(s)` +
        (inFlight ? ` · 1 batch in flight (${inFlight.frames.length} frames)` : '') +
        (pending.size ? ` · ${pending.size} pending flush` : ''),
    );
    console.table(feedLatencyStats());
    const gh = gatewayHealth.snapshot();
    if (gh.at > 0) {
      console.log(
        `[feedLatency] gateway: rtt ${gh.rttMs ?? '?'} ms · queue lag ${gh.queueLagMs ?? '?'} ms (alive beat, skew-corrected) · ` +
          `${gh.framesPerSec} frames/s · store flush p95 ${gh.flushP95Ms ?? '?'} ms (cap 33)`,
      );
    }
    // The ladder's fair-odds marker, on its own: vendor-applied-on-core → pixel.
    if (ring.some((s) => s.kind === 'opOdds')) {
      console.log('[feedLatency] opOdds only (the fair-odds marker)');
      console.table(feedLatencyStats('opOdds'));
    }
    /* eslint-enable no-console */
    return feedLatencySamples();
  };
  window.__pushLatency = () => {
    // eslint-disable-next-line no-console -- the probe's whole job
    console.table(pushLatencyStats());
    return pushLatencySamples();
  };
}
